/** Max-pain drift chart: max pain vs spot over time (two lines, ONE
 * shared strike-level axis — the overlay IS the insight). The drift mode
 * (Intraday | Day-over-Day) and its toggle are owned by OptionsTab —
 * one toggle drives this chart and the per-strike OI columns; backed by
 * /api/market/max-pain-drift (5-min snapshots captured server-side; day
 * mode = last point per day, current expiry only). */

import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { marketApi } from "@/lib/marketApi";
import { yScale, type Bounds } from "@/components/Reports/analyticsMath";
import type { DriftMode, DriftPoint, MarketIndex } from "@/types/market";
import {
  ChartTipBox,
  H,
  HoverCrosshair,
  MODE_LABELS,
  POLL_MS,
  W,
  XAxisLabels,
  YAxisLabels,
  YGrid,
  axisLabel,
  deltaTone,
  hoverIndex,
  niceTicks,
  svgPath,
  useChartHover,
  zoomSlice,
} from "./driftShared";

export function MaxPainDrift({
  index,
  mode,
  zoom = 1,
}: Readonly<{
  index: MarketIndex;
  mode: DriftMode;
  zoom?: number;
}>): ReactElement {
  const drift = useQuery({
    queryKey: ["market", "max-pain-drift", index, mode],
    queryFn: () => marketApi.maxPainDrift(index, mode),
    refetchInterval: POLL_MS,
  });
  const points = zoomSlice(drift.data?.points ?? [], zoom);
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3
          className="text-sm text-ink-secondary"
          title="Max pain drifting toward spot suggests pinning; away suggests pressure. Points captured every 5 minutes during market hours."
        >
          Max Pain Drift
        </h3>
        {points.length > 1 && <DriftDelta points={points} />}
      </div>
      {points.length === 0 ? (
        <div className="text-xs text-ink-muted">
          No drift points yet — captured every 5 minutes during market hours.
        </div>
      ) : (
        <DriftChart points={points} mode={mode} />
      )}
    </div>
  );
}

function DriftDelta({ points }: Readonly<{ points: readonly DriftPoint[] }>): ReactElement | null {
  // Where max pain has MOVED across the visible window — the "shift".
  const first = points[0];
  const last = points.at(-1);
  if (first === undefined || last === undefined) return null;
  const delta = last.max_pain - first.max_pain;
  return (
    <span
      className={cn("pill bg-surface-2 font-mono text-xs", deltaTone(delta))}
      title="Max pain move across the visible window (drifting up = writers repositioning higher)"
    >
      Δ {delta > 0 ? "+" : ""}
      {delta.toLocaleString("en-IN")}
    </span>
  );
}

function DriftChart({
  points,
  mode,
}: Readonly<{ points: readonly DriftPoint[]; mode: DriftMode }>): ReactElement {
  // ONE bounds across both series — strike-level values, so a zero
  // baseline (boundsWithZero) would flatten everything into a line.
  const bounds = strikeBounds(points);
  const solo = points.length === 1 ? points[0] : undefined;
  const ticks = niceTicks(bounds);
  const hover = useChartHover();
  const hovered = hoverIndex(hover.frac, points.length);
  return (
    <div>
      <div
        ref={hover.ref}
        className="relative"
        data-testid="max-pain-hover-area"
        onMouseMove={hover.onMove}
        onMouseLeave={hover.onLeave}
      >
        <svg
          viewBox={`0 0 ${String(W)} ${String(H)}`}
          preserveAspectRatio="none"
          className="h-32 w-full"
          role="img"
          aria-label={`Max pain vs spot (${MODE_LABELS[mode].toLowerCase()})`}
        >
          <YGrid values={ticks} bounds={bounds} height={H} />
          {solo !== undefined ? (
            <SoloDots point={solo} bounds={bounds} />
          ) : (
            <DriftLines points={points} bounds={bounds} />
          )}
          {hovered !== null && <HoverCrosshair i={hovered} n={points.length} />}
        </svg>
        <YAxisLabels values={ticks} bounds={bounds} height={H} format={formatStrikeTick} />
        {hovered !== null && hover.frac !== null && (
          <DriftTip point={points[hovered]} mode={mode} frac={hover.frac} />
        )}
      </div>
      <XAxisLabels series={points} mode={mode} />
      <ChartFooter points={points} />
    </div>
  );
}

/** Hover tooltip: the point's time plus both series' values there. */
function DriftTip({
  point,
  mode,
  frac,
}: Readonly<{
  point: DriftPoint | undefined;
  mode: DriftMode;
  frac: number;
}>): ReactElement | null {
  if (point === undefined) return null;
  return (
    <ChartTipBox frac={frac}>
      <div className="mb-1 text-ink-muted">{axisLabel(point.at, mode)}</div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-ink-muted">Max pain</span>
        <span className="font-mono text-accent">{formatStrikeTick(point.max_pain)}</span>
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-ink-muted">Spot</span>
        <span className="font-mono text-ink">{formatStrikeTick(point.spot)}</span>
      </div>
    </ChartTipBox>
  );
}

function formatStrikeTick(v: number): string {
  return Math.round(v).toLocaleString("en-IN");
}

function DriftLines({
  points,
  bounds,
}: Readonly<{ points: readonly DriftPoint[]; bounds: Bounds }>): ReactElement {
  return (
    <>
      <path
        d={svgPath(
          points.map((p) => p.spot),
          bounds,
        )}
        fill="none"
        stroke="var(--ink-secondary, #6b7280)"
        strokeWidth={1.5}
        strokeDasharray="4 3"
        strokeLinejoin="round"
      />
      <path
        d={svgPath(
          points.map((p) => p.max_pain),
          bounds,
        )}
        fill="none"
        stroke="var(--accent, #2563eb)"
        strokeWidth={2}
        strokeLinejoin="round"
      />
    </>
  );
}

function ChartFooter({ points }: Readonly<{ points: readonly DriftPoint[] }>): ReactElement {
  const last = points.at(-1);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px]">
      <LegendSwatch className="bg-accent" label="Max pain" value={last?.max_pain ?? null} />
      <LegendSwatch className="bg-ink-secondary" label="Spot" value={last?.spot ?? null} />
    </div>
  );
}

function SoloDots({
  point,
  bounds,
}: Readonly<{ point: DriftPoint; bounds: Bounds }>): ReactElement {
  return (
    <>
      <circle
        cx={4}
        cy={yScale(point.spot, bounds, H)}
        r={3}
        fill="var(--ink-secondary, #6b7280)"
      />
      <circle cx={4} cy={yScale(point.max_pain, bounds, H)} r={3} fill="var(--accent, #2563eb)" />
    </>
  );
}

function LegendSwatch({
  className,
  label,
  value,
}: Readonly<{ className: string; label: string; value: number | null }>): ReactElement {
  return (
    <span className="flex items-center gap-1.5 text-ink-secondary">
      <span className={cn("inline-block h-0.5 w-4 rounded-full", className)} />
      {label}
      {value !== null && (
        <span className="font-mono text-ink">{Math.round(value).toLocaleString("en-IN")}</span>
      )}
    </span>
  );
}

/** Tight min/max across BOTH series with a small pad; widens a flat
 * window so lines stay on-chart. Zero is deliberately NOT included. */
function strikeBounds(points: readonly DriftPoint[]): Bounds {
  const values = points.flatMap((p) => [p.max_pain, p.spot]);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = Math.max((max - min) * 0.1, 1);
  return { min: min - pad, max: max + pad };
}
